import axios from 'axios';

class FacebookBusinessService {
  constructor() {
    this.accessToken = null;
    this.pageAccessTokens = {};
  }

  async initializeAuth() {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    
    if (!appId || appId === 'your_facebook_app_id_here') {
      throw new Error('Facebook App ID not configured. Please set REACT_APP_FACEBOOK_APP_ID in your .env file.');
    }
    
    const redirectUri = `${window.location.origin}/auth/facebook/callback`;
    const state = this.generateState();
    
    // Store state for callback verification
    localStorage.setItem('facebook_oauth_state', state);
    
    const scopes = [
      'pages_read_engagement',
      'pages_show_list',
      'business_management',
      'pages_read_user_content',
      'pages_manage_posts',
      'pages_manage_engagement',
      'pages_manage_metadata',
      'instagram_basic',
      'instagram_content_publish',
      'instagram_manage_comments',
      'instagram_manage_insights',
      'ads_read',
      'ads_management',
      'business_management',
      'public_profile',
      'email',
      'user_photos',
      'user_videos'
    ].join(',');

    const authUrl = `https://www.facebook.com/v18.0/dialog/oauth?` +
      `client_id=${appId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${scopes}&` +
      `response_type=code&` +
      `state=${state}`;
    
    return {
      authUrl,
      appId,
      redirectUri,
      state
    };
  }

  generateState() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  async handleAuthCallback(code, state) {
    try {
      const storedState = localStorage.getItem('facebook_oauth_state');
      if (state && state !== storedState) {
        throw new Error('Invalid state parameter');
      }
      
      const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
      const appSecret = process.env.REACT_APP_FACEBOOK_APP_SECRET;
      const redirectUri = `${window.location.origin}/auth/facebook/callback`;

      // Exchange code for access token
      const tokenResponse = await axios.get('https://graph.facebook.com/v18.0/oauth/access_token', {
        params: {
          client_id: appId,
          client_secret: appSecret,
          redirect_uri: redirectUri,
          code: code,
        }
      });

      this.accessToken = tokenResponse.data.access_token;
      
      // Get long-lived token
      const longLivedTokenResponse = await axios.get('https://graph.facebook.com/v18.0/oauth/access_token', {
        params: {
          grant_type: 'fb_exchange_token',
          client_id: appId,
          client_secret: appSecret,
          fb_exchange_token: this.accessToken
        }
      });

      this.accessToken = longLivedTokenResponse.data.access_token;
      
      // Store tokens securely
      const tokens = {
        access_token: this.accessToken,
        token_type: tokenResponse.data.token_type || 'bearer',
        expires_in: longLivedTokenResponse.data.expires_in || 5183944, // ~60 days
        scope: tokenResponse.data.scope,
        obtained_at: Date.now()
      };
      
      localStorage.setItem('facebook_tokens', JSON.stringify(tokens));
      localStorage.removeItem('facebook_oauth_state');
      localStorage.removeItem('facebook_access_token'); // Remove old format
      
      return tokens;
    } catch (error) {
      console.error('Facebook Auth callback error:', error);
      throw error;
    }
  }

  async getBusinessPages() {
    try {
      if (!this.accessToken) {
        const storedTokens = localStorage.getItem('facebook_tokens');
        if (storedTokens) {
          const tokens = JSON.parse(storedTokens);
          this.accessToken = tokens.access_token;
          
          // Check if token is expired
          if (tokens.obtained_at && tokens.expires_in) {
            const expiresAt = tokens.obtained_at + (tokens.expires_in * 1000);
            if (Date.now() >= expiresAt) {
              throw new Error('Facebook access token expired');
            }
          }
        } else {
          throw new Error('Facebook access token not found');
        }
      }

      // Get user's Facebook pages
      const pagesResponse = await axios.get('https://graph.facebook.com/v18.0/me/accounts', {
        params: {
          access_token: this.accessToken,
          fields: 'id,name,category,about,phone,emails,website,location,hours,rating_count,overall_star_rating,access_token,instagram_business_account'
        }
      });

      const pages = pagesResponse.data.data || [];
      
      // Store page access tokens for later use
      pages.forEach(page => {
        if (page.access_token) {
          this.pageAccessTokens[page.id] = page.access_token;
        }
      });

      return this.formatBusinessData(pages);
    } catch (error) {
      console.error('Error fetching Facebook pages:', error);
      throw error;
    }
  }

  async getInstagramAccount(pageId) {
    try {
      const pageToken = this.pageAccessTokens[pageId];
      if (!pageToken) {
        throw new Error('Page access token not found');
      }

      const response = await axios.get(`https://graph.facebook.com/v18.0/${pageId}`, {
        params: {
          access_token: pageToken,
          fields: 'instagram_business_account'
        }
      });

      if (response.data.instagram_business_account) {
        const instagramId = response.data.instagram_business_account.id;
        
        // Get Instagram business account details
        const instagramResponse = await axios.get(`https://graph.facebook.com/v18.0/${instagramId}`, {
          params: {
            access_token: pageToken,
            fields: 'id,username,name,biography,followers_count,follows_count,media_count,profile_picture_url'
          }
        });

        return instagramResponse.data;
      }

      return null;
    } catch (error) {
      console.error('Error fetching Instagram account:', error);
      return null;
    }
  }

  formatBusinessData(pages) {
    return pages.map(page => ({
      id: page.id,
      name: page.name || 'Unknown Business',
      address: this.formatAddress(page.location),
      phone: page.phone || '',
      website: page.website || '',
      category: page.category || 'Business',
      description: page.about || '',
      position: {
        lat: page.location?.latitude || 0,
        lng: page.location?.longitude || 0,
      },
      rating: page.overall_star_rating || 0,
      reviewCount: page.rating_count || 0,
      hours: this.formatHours(page.hours),
      emails: page.emails || [],
      source: 'facebook',
      pageAccessToken: page.access_token,
      instagramAccount: page.instagram_business_account,
    }));
  }

  formatAddress(location) {
    if (!location) return '';
    
    const parts = [
      location.street,
      location.city,
      location.state,
      location.zip,
    ].filter(Boolean);
    
    return parts.join(', ');
  }

  formatHours(hours) {
    if (!hours) return {};
    
    const formatted = {};
    Object.keys(hours).forEach(day => {
      const dayHours = hours[day];
      formatted[day] = {
        open: dayHours.open_time || '00:00',
        close: dayHours.close_time || '23:59',
        closed: !dayHours.open_time,
      };
    });
    
    return formatted;
  }

  async getPageInsights(pageId, metrics = ['page_impressions', 'page_reach', 'page_engaged_users']) {
    try {
      const pageToken = this.pageAccessTokens[pageId];
      if (!pageToken) {
        throw new Error('Page access token not found');
      }

      const response = await axios.get(`https://graph.facebook.com/v18.0/${pageId}/insights`, {
        params: {
          access_token: pageToken,
          metric: metrics.join(','),
          period: 'day',
          since: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
          until: new Date().toISOString().split('T')[0]
        }
      });

      return response.data.data;
    } catch (error) {
      console.error('Error fetching page insights:', error);
      return [];
    }
  }

  clearTokens() {
    localStorage.removeItem('facebook_tokens');
    localStorage.removeItem('facebook_access_token'); // Remove old format
    localStorage.removeItem('facebook_oauth_state');
    this.accessToken = null;
    this.pageAccessTokens = {};
  }

  isAuthenticated() {
    const tokens = localStorage.getItem('facebook_tokens');
    if (!tokens) return false;
    
    try {
      const parsed = JSON.parse(tokens);
      // Check if token is expired
      if (parsed.obtained_at && parsed.expires_in) {
        const expiresAt = parsed.obtained_at + (parsed.expires_in * 1000);
        return Date.now() < expiresAt;
      }
      return !!parsed.access_token;
    } catch (e) {
      return false;
    }
  }
}

export default FacebookBusinessService;